"use client";

// ─────────────────────────────────────────────────────────────────────────
// Mobile plan comparison ("Mobile Pricing Page Comparison Is Hard to Read").
//
// THE PROBLEM THIS SOLVES
// The live pricing page renders four plan cards. On a wide screen they sit side
// by side and comparing is trivial; on a phone they stack, each card is roughly
// a full viewport, and the same feature never lines up across plans. Checking
// one feature across two plans meant scrolling down, back up, and holding a
// value in your head.
//
// THE SHAPE CHOSEN, AND WHY NOT THE OTHERS
// The ticket allowed three shapes: a horizontally swipeable table, sticky
// headers, or a mobile comparison table. This is the "two-plan compare mode" the
// ticket also names, because the other two have a concrete defect on a phone:
//
//   • A 4-column table at 360px gives each plan ~55px — too narrow for a price
//     or a CTA, so the decision point would move back off-screen.
//   • A horizontally scrolling table cannot keep a vertically sticky header:
//     `overflow-x: auto` makes the element a scroll container on BOTH axes, so
//     `position: sticky; top` inside it stops sticking to the page. Getting both
//     needs a synchronised second scroller, which is a lot of fragile code for a
//     worse result.
//
// Picking two plans sidesteps both: two columns are wide enough for a real price
// and a real CTA, nothing scrolls sideways, and the header sticks reliably.
//
// WHAT IS PINNED BY TESTS
// Content comes from lib/pricing/plan-comparison (itself pinned to the backend's
// entitlements). Behaviour — the two columns never collapsing to one plan, the
// CTA staying reachable, Pro keeping its emphasis — is pinned in
// tests/unit/components/pricing/mobile-plan-compare.test.tsx.
// ─────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  PRICING_TIERS,
  PLAN_BASE_CREDITS,
  centsToDollars,
  tierPerMonthCents,
  type BillingCadence,
  type PaidTierId,
  type PricingTierId,
} from "@/lib/pricing/credit-tiers";
import {
  PLAN_COMPARISON,
  COMPARISON_TIER_ORDER,
  comparisonColumnIndex,
  type ComparisonValue,
} from "@/lib/pricing/plan-comparison";

// Matches PricingTiersSection's ACCENT — emerald-700 since PANDA-2025. The
// mobile comparison and the cards it summarises must not disagree on which
// plan is highlighted, or which colour "highlighted" is.
const ACCENT = "#047857";

/** Shared column template — the header and every row use it, which is what makes
 *  a feature line up with its two values. The label column is deliberately the
 *  widest: at 360px it works out to ~120px, which fits the longest row label in
 *  three lines without pushing either value column below tap size. */
const GRID = "grid grid-cols-[1.15fr_1fr_1fr] gap-x-2";

function isPaid(tier: PricingTierId): tier is PaidTierId {
  return tier !== "free";
}

interface MobilePlanCompareProps {
  billing: BillingCadence;
  /** The signed-in user's plan; that column's CTA locks to "Current". */
  currentPlan?: string;
  /** Paid-plan CTA — the same handler the cards use, so auth gating and
   *  analytics are identical whichever surface the visitor buys from. */
  onChoosePaid: (tier: PaidTierId) => void;
  /** Free-plan CTA — same handler as the Free card. */
  onStartFree: () => void;
  /** Defaults exist so the section is useful with no interaction: Pro (the
   *  recommended plan) against Grow (the next step up) is the comparison most
   *  visitors are actually making. */
  initialLeft?: PricingTierId;
  initialRight?: PricingTierId;
}

export function MobilePlanCompare({
  billing,
  currentPlan,
  onChoosePaid,
  onStartFree,
  initialLeft = "pro",
  initialRight = "grow",
}: MobilePlanCompareProps) {
  const [left, setLeft] = useState<PricingTierId>(initialLeft);
  const [right, setRight] = useState<PricingTierId>(initialRight);

  // Picking the plan that is already in the other column would collapse the
  // comparison to one plan — the exact problem this section exists to fix. Swap
  // instead, so the two columns always hold two different plans.
  function pick(side: "left" | "right", next: PricingTierId) {
    if (side === "left") {
      if (next === right) setRight(left);
      setLeft(next);
    } else {
      if (next === left) setLeft(right);
      setRight(next);
    }
  }

  return (
    // scroll-mt clears the sticky nav when the "Compare plans" jump link lands.
    <div id="compare-plans" className="mt-16 scroll-mt-20 lg:hidden">
      <h2 className="font-[family-name:var(--font-rubik),sans-serif] text-2xl font-medium text-foreground">
        Compare plans
      </h2>
      <p className="mt-1 font-[family-name:var(--font-rubik),sans-serif] text-sm text-[#475569] dark:text-muted-foreground">
        Pick two plans to see them side by side.
      </p>

      {/* Sticky header — plan name, price and CTA stay on screen for the whole
          length of the table, so the visitor never has to scroll back to the
          cards to see what a plan costs or to buy it. `top-16` clears the
          page's sticky nav (h-10 controls + py-3 ≈ 64px). */}
      <div className="sticky top-16 z-30 -mx-4 mt-4 bg-background px-4 pb-3 pt-3 shadow-[0px_3px_7.5px_rgba(189,189,189,0.2)] sm:-mx-8 sm:top-18 sm:px-8 dark:border-b dark:border-border dark:shadow-none">
        {/* items-stretch, not items-start: the columns must be equal height so
            the two CTAs land on the same line even when one plan's price wraps
            and the other's doesn't. */}
        <div className={cn(GRID, "items-stretch")}>
          <span className="self-center font-[family-name:var(--font-rubik),sans-serif] text-xs font-medium uppercase tracking-wide text-[#77869D] dark:text-muted-foreground">
            Feature
          </span>
          <ColumnHeader
            side="left"
            tier={left}
            billing={billing}
            currentPlan={currentPlan}
            onPick={pick}
            onChoosePaid={onChoosePaid}
            onStartFree={onStartFree}
          />
          <ColumnHeader
            side="right"
            tier={right}
            billing={billing}
            currentPlan={currentPlan}
            onPick={pick}
            onChoosePaid={onChoosePaid}
            onStartFree={onStartFree}
          />
        </div>
      </div>

      {/* Rows — one line per feature, both plans' values on that same line. */}
      <div className="mt-2">
        {PLAN_COMPARISON.map((section) => (
          <div key={section.heading}>
            <p className="mt-6 mb-1 font-[family-name:var(--font-rubik),sans-serif] text-base font-medium text-foreground">
              {section.heading}
            </p>
            {section.rows.map((row) => (
              <div
                key={row.label}
                className={cn(
                  GRID,
                  "items-center border-b border-[#E2E8F0] py-3 dark:border-border",
                )}
              >
                <span
                  className="min-w-0 wrap-break-word font-[family-name:var(--font-rubik),sans-serif] text-sm leading-5 text-foreground"
                  title={row.note}
                >
                  {row.label}
                </span>
                <Cell
                  value={row.values[comparisonColumnIndex(left)]}
                  label={row.label}
                  planName={PRICING_TIERS[left].name}
                  highlighted={left === "pro"}
                />
                <Cell
                  value={row.values[comparisonColumnIndex(right)]}
                  label={row.label}
                  planName={PRICING_TIERS[right].name}
                  highlighted={right === "pro"}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function ColumnHeader({
  side,
  tier,
  billing,
  currentPlan,
  onPick,
  onChoosePaid,
  onStartFree,
}: {
  side: "left" | "right";
  tier: PricingTierId;
  billing: BillingCadence;
  currentPlan?: string;
  onPick: (side: "left" | "right", next: PricingTierId) => void;
  onChoosePaid: (tier: PaidTierId) => void;
  onStartFree: () => void;
}) {
  const def = PRICING_TIERS[tier];
  const isCurrent = !!currentPlan && currentPlan.toLowerCase() === tier;
  // "from" is load-bearing: paid plans price off a credit dropdown, so this is
  // the plan's cheapest rung (its included base), not a fixed price. It matches
  // the "Credits included" row directly below.
  const priceLabel = isPaid(tier)
    ? centsToDollars(tierPerMonthCents(tier, PLAN_BASE_CREDITS[tier], billing))
    : "$0";
  const highlighted = tier === "pro";

  return (
    <div
      data-testid={`compare-col-${side}`}
      className={cn(
        "flex min-w-0 flex-col gap-1.5 rounded-xl p-2",
        // The recommended plan keeps its emphasis in this layout too, so the
        // signal isn't lost the moment the visitor leaves the cards.
        highlighted && "bg-emerald-50 dark:bg-emerald-500/[0.08]",
      )}
    >
      {/* The badge slot is always present, even when empty. Rendering it only
          for Pro made that column taller, so its plan picker sat lower than the
          other column's. */}
      <span className="flex h-4 items-center">
        {highlighted && (
          <span
            className="rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase leading-3 text-white"
            style={{ background: ACCENT }}
          >
            Popular
          </span>
        )}
      </span>

      <PlanPicker
        label={`${side === "left" ? "First" : "Second"} plan to compare`}
        value={tier}
        highlighted={highlighted}
        onChange={(next) => onPick(side, next)}
      />

      {/* min-h reserves two lines: "from $81/mo" fits on one line but
          "from $243/mo" does not, and an unreserved line makes the shorter
          column's price and CTA sit higher than its neighbour's. */}
      <p className="min-h-10 font-[family-name:var(--font-rubik),sans-serif] text-sm font-medium leading-5 text-foreground">
        {isPaid(tier) && (
          <span className="font-normal text-[#77869D] dark:text-muted-foreground">
            from{" "}
          </span>
        )}
        {priceLabel}
        <span className="font-normal text-[#77869D] dark:text-muted-foreground">
          /mo
        </span>
      </p>

      <button
        type="button"
        // The column holds two buttons (this and the plan picker), so tests
        // need an unambiguous handle on the one that starts checkout.
        data-testid={`compare-cta-${side}`}
        disabled={isCurrent}
        aria-current={isCurrent ? "true" : undefined}
        // The visible label has to stay short to fit a ~100px column, which
        // leaves "Choose" sitting next to another "Choose" with nothing to tell
        // them apart. An explicit label carries the plan name instead of an
        // sr-only span: accessible-name computation concatenates child text
        // WITHOUT inserting whitespace, so "Choose" + " Pro" is announced as
        // "ChoosePro".
        aria-label={
          isCurrent
            ? `${def.name} — current plan`
            : isPaid(tier)
              ? `Choose ${def.name}`
              : "Start free"
        }
        onClick={() => (isPaid(tier) ? onChoosePaid(tier) : onStartFree())}
        className={cn(
          // mt-auto pins the CTA to the bottom of an equal-height column, so
          // the two buttons align no matter what happens above them.
          "mt-auto inline-flex h-9 w-full items-center justify-center rounded-full px-2 font-[family-name:var(--font-rubik),sans-serif] text-xs font-medium text-white transition-all disabled:cursor-not-allowed disabled:opacity-60",
          highlighted
            ? "hover:brightness-110"
            : "bg-[#080B12] hover:brightness-110 dark:bg-foreground dark:text-background",
        )}
        style={highlighted ? { background: ACCENT } : undefined}
      >
        {isCurrent ? "Current" : isPaid(tier) ? "Choose" : "Start free"}
      </button>
    </div>
  );
}

/**
 * Plan picker for one comparison column.
 *
 * This replaced a native <select>. A native select is the right default on a
 * phone — it opens the OS picker — but this section also renders on any window
 * narrower than `lg`, where desktop Chrome draws an unstyled grey popup that
 * looks nothing like the rest of the page. Four options is small enough that a
 * styled listbox is the better trade: one appearance everywhere, tap targets we
 * control, and the selected plan marked rather than merely highlighted.
 *
 * Deliberately hand-rolled rather than built on components/ui/dropdown-menu:
 * that primitive is a MENU (menu/menuitem roles), and picking which plan a
 * column shows is a value choice, so listbox/option is the honest mapping.
 */
function PlanPicker({
  label,
  value,
  highlighted,
  onChange,
}: {
  label: string;
  value: PricingTierId;
  highlighted: boolean;
  onChange: (next: PricingTierId) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close on outside click / Escape. Without this the popup survives a tap on
  // the other column's picker, leaving two open lists over the table.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex h-9 w-full items-center justify-between gap-1 rounded-lg border px-2 font-[family-name:var(--font-rubik),sans-serif] text-sm font-medium text-foreground transition-colors outline-none focus-visible:ring-2 focus-visible:ring-emerald-600",
          highlighted
            ? "border-emerald-600/40 bg-background"
            : "border-border bg-background hover:bg-card",
        )}
      >
        <span className="truncate">{PRICING_TIERS[value].name}</span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
          aria-hidden
        />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={label}
          // Wider than the trigger so plan names never truncate in the list,
          // and above the sticky header's own stacking context.
          className="absolute left-0 top-full z-40 mt-1 min-w-full overflow-hidden rounded-xl border border-border bg-background py-1 shadow-lg"
        >
          {COMPARISON_TIER_ORDER.map((id) => {
            const selected = id === value;
            return (
              <li key={id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    onChange(id);
                    setOpen(false);
                  }}
                  className={cn(
                    "flex h-9 w-full items-center justify-between gap-2 px-3 text-left font-[family-name:var(--font-rubik),sans-serif] text-sm transition-colors hover:bg-card",
                    selected ? "font-medium text-foreground" : "text-foreground/80",
                  )}
                >
                  {PRICING_TIERS[id].name}
                  {selected && (
                    <Check
                      className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400"
                      aria-hidden
                    />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Cell({
  value,
  label,
  planName,
  highlighted,
}: {
  value: ComparisonValue;
  label: string;
  planName: string;
  highlighted: boolean;
}) {
  return (
    <span
      className={cn(
        "flex min-w-0 items-center justify-center px-1 text-center",
        // The tint has to span the row's full height (the row's own py-3 is
        // cancelled and re-added here) so consecutive rows join into one
        // continuous column stripe. Left as a per-cell box it renders as a
        // stack of pills, which reads as a row of buttons rather than as the
        // recommended plan's column.
        highlighted &&
          "-my-3 self-stretch bg-emerald-50 py-3 dark:bg-emerald-500/[0.08]",
      )}
    >
      {/* Without this the row reads as "Custom domain, tick, dash" — which value
          belongs to which plan is only conveyed visually by column position. */}
      <span className="sr-only">
        {planName} {label}:{" "}
      </span>
      {typeof value === "boolean" ? (
        value ? (
          <>
            <Check className="h-4 w-4 shrink-0 text-[#12B76A]" aria-hidden />
            <span className="sr-only">Included</span>
          </>
        ) : (
          <>
            <Minus
              className="h-4 w-4 shrink-0 text-[#CBD5E1] dark:text-muted-foreground"
              aria-hidden
            />
            <span className="sr-only">Not included</span>
          </>
        )
      ) : (
        <span className="wrap-break-word font-[family-name:var(--font-rubik),sans-serif] text-xs leading-4 text-foreground">
          {value}
        </span>
      )}
    </span>
  );
}
